import {
  Avatar,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
} from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { useState, useEffect } from "react";
import HeaderAppBar from "./appbar";
import { useNavigate } from "react-router-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { BackendBaseUrl, useAuth, ORANGE, ORANGE_HOVER, BLACK, PREMIUM_SHADOW, CARD_SHADOW } from "./global_varibles";
import PercentIcon from "@mui/icons-material/Percent";
import { alpha } from "@mui/material/styles"; // added


export function CalculateDiscount({ Total, final }: { Total: number; final: number }) {
  const perc_dis = Total ? (((Total - final) / Total) * 100).toFixed(2) : "0.00";
  return perc_dis;
}

type CategoryDetails = {
  item_price: number;
  quantity: number;
  type_id?: string | number;
  serv_id?: string | number;
};

type OrderedItem = {
  item_name: string;
  item_url: string;
  categories: Record<string, CategoryDetails>;
};

type OrderedDetailsType = {
  [serviceType: string]: {
    [itemId: string]: OrderedItem;
  };
};

type NewItemType = {
  itemName: string;
  quantity: number | string;
  unitPrice: number | string;
  totalPrice: number | string;
};

interface OrderSummaryProps {
  ordered_details: OrderedDetailsType;
  newItems: NewItemType[];
  is_edit?: boolean;
}

interface SelectedItemState {
  itemName: string;
  quantity: number;
  itemPrice: number;
}

export function OrderSummary({ ordered_details, newItems, is_edit }: OrderSummaryProps) {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SelectedItemState>({
    itemName: "",
    quantity: 0,
    itemPrice: 0,
  });

  const handleRowClick = (item: { item_name: string; categories: CategoryDetails }) => {
    if (is_edit) {
      setSelectedItem({
        itemName: item.item_name,
        quantity: item.categories.quantity,
        itemPrice: item.categories.item_price,
      });
      setOpenDialog(true);
    }
  };

  const handleClose = () => setOpenDialog(false);

  return (
    <Box>
      {Object.keys(ordered_details).map((serviceType) => {
        const items = ordered_details[serviceType];
        return (
          <Box key={serviceType} sx={{ width: "100%", mb: 4, maxWidth: 1000, mx: "auto" }}>
            <Typography variant="h6" sx={{ textAlign: "left", ml: 1.5, fontWeight: 700 }}>
              {serviceType}
            </Typography>
            <TableContainer 
              component={Paper} 
              elevation={0} 
              sx={{ 
                border: `1px solid ${alpha(ORANGE, 0.1)}`, 
                borderRadius: 3, 
                overflowX: "auto",
                boxShadow: CARD_SHADOW,
                backgroundColor: "#fff"
              }}
            >
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(ORANGE, 0.04), borderBottom: `2px dashed ${alpha(ORANGE, 0.1)}` }}>
                  <TableRow>
                    <TableCell sx={{ minWidth: 100, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }}>Item</TableCell>
                    <TableCell sx={{ minWidth: 80, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Category</TableCell>
                    <TableCell sx={{ minWidth: 50, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Qty</TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Price</TableCell>
                    <TableCell sx={{ minWidth: 70, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(items).map((itemId) => {
                    const itemDetails = items[itemId];
                    return Object.keys(itemDetails.categories).map((category) => {
                      const details = itemDetails.categories[category];
                      const categoryTotal = details.quantity * details.item_price;
                      return (
                        <TableRow
                          key={`${itemId}-${category}`}
                          onClick={() => handleRowClick({ item_name: itemDetails.item_name, categories: details })}
                          sx={{ 
                            cursor: is_edit ? "pointer" : "default",
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": is_edit ? { backgroundColor: alpha(ORANGE, 0.02) } : {}
                          }}
                        >
                          <TableCell sx={{ borderBottom: `1px solid ${alpha(ORANGE, 0.05)}`, px: { xs: 1, sm: 2 } }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar 
                                src={itemDetails.item_url} 
                                variant="rounded"
                                sx={{ width: 28, height: 28, mr: 1.5, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} 
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: BLACK }}>
                                {itemDetails.item_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                              {category}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: ORANGE }}>
                              {details.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                              ₹{details.item_price}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: BLACK }}>
                              ₹{parseFloat(categoryTotal.toFixed(2))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {newItems && newItems.length > 0 && (
        <Box sx={{ width: "100%", mb: 4, maxWidth: 1000, mx: "auto" }}>
          <Typography variant="h6" sx={{ textAlign: "left", ml: 1.5, fontWeight: 700 }}>Others</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${alpha(ORANGE, 0.2)}`, borderRadius: 2, overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: alpha(ORANGE, 0.06) }}>
                <TableRow>
                  <TableCell sx={{ minWidth: 100, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }}>Item</TableCell>
                  <TableCell sx={{ minWidth: 50, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Qty</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Price</TableCell>
                  <TableCell sx={{ minWidth: 70, fontWeight: 700, color: BLACK, fontSize: "0.85rem", textTransform: "uppercase", px: { xs: 1, sm: 2 } }} align="center">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ borderBottom: `1px solid ${alpha(ORANGE, 0.05)}`, px: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: BLACK }}>{item.itemName}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: ORANGE }}>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        ₹{item.unitPrice || (Number(item.quantity) ? (Number(item.totalPrice) / Number(item.quantity)).toFixed(2) : 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: `1px dashed ${alpha(ORANGE, 0.1)}`, px: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: BLACK }}>₹{item.totalPrice}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleClose}>
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <TextField label="Item Name" value={selectedItem.itemName} fullWidth margin="dense" variant="outlined" />
          <TextField label="Quantity" value={selectedItem.quantity} fullWidth margin="dense" variant="outlined" type="number" />
          <TextField label="Price" value={selectedItem.itemPrice} fullWidth margin="dense" variant="outlined" type="number" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

interface StoredItemLeaf {
  price: number;
  quantity: number;
}

type StoredItemsType = Record<string, Record<string, Record<string, StoredItemLeaf>>>;

interface StructuredData {
  order_data: {
    [category: string]: {
      [itemId: string]: {
        item_name: string;
        item_url: string;
        categories: {
          [subCategory: string]: { type_id?: string | number; serv_id?: string | number };
        };
      };
    };
  };
}

interface ShowOrderedDetailsProps {
  ordered_details: any; // Replace 'any' with proper type definition if available
}

export const ShowOrderedDetails: React.FC<ShowOrderedDetailsProps> = ({  }) => {
  const navigate = useNavigate();
  const { user_data } = useAuth();
  const [orderedDetails, setOrderedDetails] = useState<OrderedDetailsType>({});
  const [newItems, setNewItems] = useState<NewItemType[]>([]);
  const [finalTotal, setFinalTotal] = useState(0);
  const [Total, setTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const adminId = user_data?.id ?? "";

  useEffect(() => {
    const storedItemsRaw = localStorage.getItem("current_order_items");
    const storedItems: StoredItemsType = storedItemsRaw
      ? (JSON.parse(storedItemsRaw) as StoredItemsType)
      : {};

    const structuredDataRaw = localStorage.getItem("structuredData");
    const structuredData: StructuredData = structuredDataRaw
      ? (JSON.parse(structuredDataRaw) as StructuredData)
      : { order_data: {} };

    const newItemsRaw = localStorage.getItem("new_items");
    const new_items: NewItemType[] = newItemsRaw ? JSON.parse(newItemsRaw) : [];
    setNewItems(new_items);

    if (storedItems && structuredData.order_data) {
      const order_data = structuredData.order_data;
      const built: OrderedDetailsType = {};

      Object.keys(storedItems).forEach((category) => {
        const categoryItems = storedItems[category];
        Object.keys(categoryItems).forEach((item_id) => {
          const subCategories = categoryItems[item_id];
          if (subCategories) {  // Add null check
            const chosenSubs = Object.entries(subCategories)
              .filter(([, details]) => 
                details && // Add null check
                typeof details === 'object' && 
                'quantity' in details && 
                details.quantity > 0
              );
            
            if (chosenSubs.length > 0) {
              if (!built[category]) built[category] = {};
              const itemDetails = order_data[category]?.[item_id];
              if (itemDetails) {
                built[category][item_id] = {
                  item_name: itemDetails.item_name,
                  item_url: itemDetails.item_url,
                  categories: {},
                };
                
                chosenSubs.forEach(([subCategory, details]) => {
                  const meta = itemDetails.categories[subCategory];
                  if (meta && details) {  // Add null check
                    built[category][item_id].categories[subCategory] = {
                      item_price: details.price,
                      quantity: details.quantity,
                      type_id: meta.type_id,
                      serv_id: meta.serv_id,
                    };
                  }
                });
              }
            }
          }
        });
      });

      setOrderedDetails(built);
    }
  }, []);

  useEffect(() => {
    let total = 0;
    Object.keys(orderedDetails).forEach((serviceType) => {
      const items = orderedDetails[serviceType];
      Object.keys(items).forEach((itemId) => {
        const itemDetails = items[itemId];
        Object.keys(itemDetails.categories).forEach((cat) => {
          const details = itemDetails.categories[cat];
          total += details.quantity * details.item_price;
        });
      });
    });
    newItems.forEach((item) => {
      total += Number(item.totalPrice) || 0;
    });
    const finalCalculated = total + otherCharges - (discount / 100) * (total + otherCharges);
    setFinalTotal(parseFloat(finalCalculated.toFixed(2)));
    setTotal(parseFloat(total.toFixed(2)));
  }, [orderedDetails, newItems, otherCharges, discount]);

  const handleSubmit = async () => {
    if (!adminId) {
      alert("Not authorized. Please sign in again.");
      return;
    }
    const customer_details = JSON.parse(localStorage.getItem("user_details") || "{}");
    if (finalTotal > Total + otherCharges) {
      alert("Please check Order Value");
      return;
    }
    const backendRoute = `${BackendBaseUrl}create-order`;
    const data_to_create = {
      customer_details,
      admin_id: adminId,
      order_details: orderedDetails,
      others: newItems.map(item => ({
        ...item,
        unitPrice: Number(item.unitPrice) || 0,
        quantity: Number(item.quantity) || 0,
        totalPrice: Number(item.totalPrice) || 0
      })),
      total_price: Total,
      discount,
      other_charges: otherCharges,
    };
    setIsLoading(true);
    try {
      const response = await fetch(backendRoute, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data_to_create),
      });
      const response_data = await response.json();
      if (response_data.status === "success") {
        localStorage.removeItem("current_order_items");
        localStorage.removeItem("structuredData");
        localStorage.removeItem("user_details");
        localStorage.removeItem("new_items");
        navigate(`/order-details/${response_data.order_id}`);
      } else {
        console.error("Failed to submit order", response_data.message);
      }
    } catch (error) {
      console.error("Error submitting order:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtherChargesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOtherCharges(value === '' ? 0 : Number(value));
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscount(value === '' ? 0 : Number(value));
  };

  return (
    <Box>
      <HeaderAppBar />
      <Box sx={{ minHeight: "100vh", width: "100%" }}>
        <Paper
          elevation={5}
          sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            mt: 2,
            p: { xs: 1.5, sm: 2.5 },
            overflowX: { xs: "auto", sm: "hidden" },
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 1000,
                mx: "auto",
                py: 0.5
              }}
            >
              <Button
                onClick={() => navigate(-1)}
                startIcon={<ChevronLeftRoundedIcon />}
                aria-label="Go back"
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  color: "#0b0f14",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 20,
                  px: 1.25,
                  "&:hover": { backgroundColor: alpha(ORANGE, 0.1), color: ORANGE }
                }}
              >
                Back
              </Button>

              <Typography variant="h5" component="div" align="center" sx={{ mb: 1, fontWeight: 700 }}>
                Order Summary
              </Typography>
            </Box>
          </Box>

          <OrderSummary ordered_details={orderedDetails} newItems={newItems} />

          {/* Totals card */}
          <Box
            sx={{
              maxWidth: 1000,
              mx: "auto",
              mt: 3,
              mb: 10, // space above sticky submit
            }}
          >
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                border: `1px solid ${alpha(ORANGE, 0.15)}`, 
                borderRadius: 4,
                backgroundColor: "#fff",
                boxShadow: PREMIUM_SHADOW,
                backgroundImage: `radial-gradient(${alpha(ORANGE, 0.05)} 1px, transparent 1px)`,
                backgroundSize: '20px 20px',
              }}
            >
              {/* Order Value */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>Order Value: {Total}</Typography>
              </Box>

              {/* Other Charges and Discount - Side by side on mobile */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 2,
                  alignItems: { sm: "center" }
                }}
              >
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  justifyContent: "space-between",
                  flex: { sm: 1 }
                }}>
                  <Typography sx={{ mr: 1, minWidth: "fit-content" }}>Other Charges</Typography>
                  <TextField
                    type="number"
                    value={otherCharges === 0 ? '' : otherCharges}
                    onChange={handleOtherChargesChange}
                    size="small"
                    placeholder="0"
                    sx={{ width: 100 }}
                  />
                </Box>

                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  justifyContent: "space-between",
                  flex: { sm: 1 }
                }}>
                  <Typography sx={{ mr: 1, minWidth: "fit-content" }}>Discount</Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      type="number"
                      value={discount === 0 ? '' : discount}
                      onChange={handleDiscountChange}
                      size="small"
                      placeholder="0"
                      sx={{ width: 100, mr: 0.5 }}
                    />
                    <PercentIcon fontSize="small" />
                  </Box>
                </Box>
              </Box>

              {/* Total */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: { xs: "space-between", sm: "flex-end" },
                pt: 1,
                borderTop: `1px solid ${alpha(ORANGE, 0.2)}`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>Total:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{finalTotal}</Typography>
              </Box>
            </Paper>
          </Box>

          {/* Sticky submit (matches Create Order CTA styling) */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || !adminId}
              sx={{
                width: { xs: "100%", sm: "60%" },
                position: "fixed",
                bottom: 24,
                left: { sm: "20%" },
                zIndex: 1000,
                borderRadius: 12,
                py: 2,
                backgroundColor: ORANGE,
                fontWeight: 800,
                fontSize: "1.1rem",
                textTransform: "none",
                boxShadow: `0 12px 24px ${alpha(ORANGE, 0.4)}`,
                "&:hover": { 
                  backgroundColor: ORANGE_HOVER,
                  boxShadow: `0 16px 32px ${alpha(ORANGE, 0.5)}`,
                  transform: "translateY(-2px)"
                },
                "&.Mui-disabled": {
                  backgroundColor: alpha(ORANGE, 0.3),
                  color: alpha("#fff", 0.7)
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              }}
            >
              {isLoading ? "Submitting..." : "Place Final Order"}
            </Button>
          </Box>

          {isLoading && (
            <div className="spinner-container">
              <ThreeDots color="#3498db" height={80} width={80} ariaLabel="loading" />
            </div>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default ShowOrderedDetails;
